'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import type { View } from 'vega';
import { Box, VStack } from '@terra/ui-styled-system';
import { BodyDefault, MetaDefault } from '../../../../../components/typography';
import { JSONRenderer } from '../../ToolCallsRenderers/renderers/JSONRenderer';
import { useThemeMode } from '../../../../theme/useThemeMode';
import { buildResponsiveVegaLiteSpec } from './vega-lite-visualization.utils';
import {
  toVegaLiteEmbedSpec,
  type VegaLiteJsonObject,
} from './vega-lite.types';

type VegaLiteChartProps = {
  spec: VegaLiteJsonObject;
  description?: string;
};

export function VegaLiteChart({ spec, description }: VegaLiteChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { mode } = useThemeMode();
  const [embedError, setEmbedError] = useState<string | null>(null);
  const specKey = useMemo(() => JSON.stringify(spec), [spec]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const stableSpec = JSON.parse(specKey) as VegaLiteJsonObject;
    const embedSpec = toVegaLiteEmbedSpec(
      buildResponsiveVegaLiteSpec(stableSpec),
    );

    let cancelled = false;
    let view: View | undefined;
    let resizeFrameId: number | undefined;

    const scheduleResize = () => {
      if (!view || cancelled) {
        return;
      }

      if (resizeFrameId !== undefined) {
        cancelAnimationFrame(resizeFrameId);
      }

      resizeFrameId = requestAnimationFrame(() => {
        if (!view || cancelled) {
          return;
        }

        void view.resize().runAsync();
      });
    };

    const renderChart = async () => {
      container.replaceChildren();
      setEmbedError(null);

      try {
        const { default: vegaEmbed } = await import('vega-embed');
        const result = await vegaEmbed(container, embedSpec, {
          actions: false,
          renderer: 'svg',
        });

        if (cancelled) {
          result.view.finalize();

          return;
        }

        view = result.view;
        await view.resize().runAsync();
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }

        setEmbedError(
          error instanceof Error ? error.message : 'Failed to render chart',
        );
      }
    };

    void renderChart();

    const observer = new ResizeObserver(scheduleResize);

    observer.observe(container);

    return () => {
      cancelled = true;
      observer.disconnect();
      if (resizeFrameId !== undefined) {
        cancelAnimationFrame(resizeFrameId);
      }

      view?.finalize();
      container.replaceChildren();
    };
  }, [specKey, mode]);

  return (
    <VStack alignItems="stretch" gap={2} width="100%">
      {description ? (
        <MetaDefault color="fg.muted">{description}</MetaDefault>
      ) : null}
      {embedError ? (
        <>
          <BodyDefault color="fg.error">{embedError}</BodyDefault>
          <JSONRenderer data={spec} />
        </>
      ) : null}
      <Box
        ref={containerRef}
        width="100%"
        minWidth="0"
        minHeight="280px"
        overflowX="auto"
        bg="bg.canvas"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="md"
        p={2}
        css={{
          '& > .vega-embed': {
            width: '100%',
          },
          '& svg': {
            display: 'block',
            maxWidth: '100%',
          },
        }}
      />
    </VStack>
  );
}
